import { QuizConfig, QuizOption } from "../type";

/**
 * Valid placeholder keys that can be used in dynamic text
 */
export type ValidPlaceholderKey =
  | "goal"
  | "goal_label"
  | "attributes"
  | "attributes_label"
  | "skills"
  | "skills_label"
  | "flexibility"
  | "flexibility_label"
  | "career-stage"
  | "career-stage_label"
  | "level"
  | "level_label";

/**
 * Validates placeholders in text against quiz configuration
 * Returns array of invalid placeholder names found
 */
export const validatePlaceholders = (
  text: string,
  quizConfig: QuizConfig,
): string[] => {
  if (!text) return [];

  const placeholderRegex = /\{([^}]+)\}/g;
  const matches = Array.from(text.matchAll(placeholderRegex));
  const invalidPlaceholders: string[] = [];

  // Get all valid keys from quiz sections
  const validKeys = new Set<string>();
  quizConfig.sections.forEach((section) => {
    const key = section.searchFilterKey || section.key;
    if (key) {
      validKeys.add(key);
      validKeys.add(`${key}_label`);
    }
  });

  for (const match of matches) {
    const placeholder = match[1];
    if (!validKeys.has(placeholder)) {
      invalidPlaceholders.push(placeholder);
    }
  }

  return invalidPlaceholders;
};

/**
 * Processes dynamic text by replacing placeholders with actual answer values
 * Placeholders format: {question_key} or {question_key_label}
 * Example: "Help us find the best path to {goal_label}" → "Help us find the best path to advance my career"
 */
export const processDynamicText = (
  text: string,
  answers: Record<string, string | string[]>,
  quizConfig: QuizConfig,
): string => {
  if (!text) return text;

  let processedText = text;

  // Validate placeholders in development
  if (process.env.NODE_ENV === "development") {
    const invalidPlaceholders = validatePlaceholders(text, quizConfig);
    if (invalidPlaceholders.length > 0) {
      console.warn(
        `Invalid placeholders found in text "${text}":`,
        invalidPlaceholders,
      );
    }
  }

  // Find all placeholders in the format {key} or {key_label}
  const placeholderRegex = /\{([^}]+)\}/g;
  const matches = text.matchAll(placeholderRegex);

  for (const match of matches) {
    const fullPlaceholder = match[0]; // e.g., "{goal}" or "{goal_label}"
    const placeholder = match[1]; // e.g., "goal" or "goal_label"

    // Check if this is a request for the label version
    const isLabelRequest = placeholder.endsWith("_label");
    const answerKey = isLabelRequest
      ? placeholder.replace("_label", "")
      : placeholder;

    const answerValue = answers[answerKey];

    if (answerValue) {
      let replacementText = "";

      if (isLabelRequest) {
        // Find the human-readable label for the answer value
        const section = quizConfig.sections.find(
          (s) => (s.searchFilterKey || s.key) === answerKey,
        );

        if (section?.options) {
          const answerValues = Array.isArray(answerValue)
            ? answerValue
            : [answerValue];
          const labels = answerValues
            .map((val) => {
              const option = section.options?.find(
                (opt: QuizOption) =>
                  opt.value === val ||
                  opt.searchFilterValue === val ||
                  opt.label === val,
              );
              return option?.message || option?.label;
            })
            .filter(Boolean);

          replacementText =
            labels.length > 1
              ? labels.slice(0, -1).join(", ") + " and " + labels.slice(-1)
              : labels[0] || "";
        } else {
          // For dynamic sections without static options, the value IS the label
          const answerValues = Array.isArray(answerValue)
            ? answerValue
            : [answerValue];
          replacementText =
            answerValues.length > 1
              ? answerValues.slice(0, -1).join(", ") +
                " and " +
                answerValues.slice(-1)
              : answerValues[0] || "";
        }
      } else {
        // Use the raw value
        replacementText = Array.isArray(answerValue)
          ? answerValue.join(", ")
          : answerValue;
      }

      processedText = processedText.replace(fullPlaceholder, replacementText);
    }
  }

  return processedText;
};
